/**
 * Google Analytics Integration
 * 
 * This component loads Google Analytics only when the user has consented to analytics cookies.
 * 
 * TO CONFIGURE:
 * 1. Replace 'G-XXXXXXXXXX' with your actual Google Analytics Measurement ID
 * 2. The component automatically respects the cookie consent preferences
 */

import { useEffect } from 'react';
import { useCookieConsent } from './CookieConsent';
import logger from '@/utils/logger';

// Replace with your actual Google Analytics Measurement ID
const GA_MEASUREMENT_ID = 'G-XXXXXXXXXX';

export default function GoogleAnalytics() {
  const consent = useCookieConsent();

  useEffect(() => {
    // Only load GA if analytics consent is given and we have a valid ID
    if (!consent?.analytics || GA_MEASUREMENT_ID === 'G-XXXXXXXXXX') {
      // If consent was revoked, remove GA cookies
      if (consent && !consent.analytics) {
        // Clear GA cookies
        document.cookie.split(';').forEach(cookie => {
          const name = cookie.split('=')[0].trim();
          if (name.startsWith('_ga') || name.startsWith('_gid')) {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
          }
        });
      }
      return;
    }

    // Check if GA is already loaded
    if (window.gtag) return;

    // Load Google Analytics script
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.async = true;
    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;
    
    gtag('js', new Date());
    gtag('config', GA_MEASUREMENT_ID, {
      anonymize_ip: true, // GDPR compliance - anonymize IP addresses
      cookie_flags: 'SameSite=None;Secure',
    });

    logger.log('Google Analytics loaded with consent');

    // Cleanup function
    return () => {
      // GA doesn't have a clean unload, but we clear cookies on consent change
    };
  }, [consent?.analytics]);

  return null; // This component doesn't render anything
}

// Helper function to track custom events (can be imported and used elsewhere)
export function trackEvent(eventName, eventParams = {}) {
  if (window.gtag && GA_MEASUREMENT_ID !== 'G-XXXXXXXXXX') {
    window.gtag('event', eventName, eventParams);
  }
}

// Helper function to track page views (useful for SPA navigation)
export function trackPageView(pagePath, pageTitle) {
  if (window.gtag && GA_MEASUREMENT_ID !== 'G-XXXXXXXXXX') {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: pagePath,
      page_title: pageTitle,
    });
  }
}
